/**
 * Grant Eligibility Matcher
 *
 * Determines which grants a company is eligible for based on
 * industry, location, founding year, employee count, revenue,
 * profitability, deadline status, and time estimate.
 *
 * Extracted from: src/utils/upgradeListUtils.js (find_grants)
 * Also had a legacy duplicate in: src/App.js ~line 1934
 *
 * Dependencies:
 *   - countryCodes, regionCodes, industries from helperData.js
 *     countryCodes: { "CA": "canada", "US": "united states", ... }
 *     regionCodes:  { CA: { "ON": "ontario", "BC": "british columbia", ... }, US: { ... } }
 *     industries:   { "Technology": ["saas", "software", ...], "Healthcare": [...], ... }
 *
 * Airtable fields read from each grant record (grant.fields.*):
 *   Eligible_Industry       - comma-separated industries (e.g. "Technology, Healthcare") or "All"
 *   Eligible_Founded        - required minimum years in operation (0 = no requirement)
 *   Eligible_Region         - comma-separated regions (e.g. "Canada, Ontario")
 *   Eligible_Employees      - minimum employee count
 *   Eligible_Revenue        - minimum revenue
 *   Eligible_Profitable     - "Yes" or "No"
 *   Eligible_Company_Type   - comma-separated legal structures; empty matches any company
 *   Eligible_Women          - "Yes" requires a women-led company
 *   Eligible_Indigenous     - "Yes" requires an Indigenous company
 *   Deadline                - date string, "Continuous", "Closed", etc.
 *   TimeEstimateToComplete  - estimated days to complete (max 90)
 *   Amount                  - grant dollar amount (string like "$50,000" or number)
 *   pSuccess                - probability of success (0-1, defaults to 0.65)
 *   Name                    - grant name
 */

import { filterActiveGrants } from './grantArchive.js';

const CANADIAN_PROVINCES = new Set([
  'alberta',
  'british columbia',
  'manitoba',
  'new brunswick',
  'newfoundland and labrador',
  'northwest territories',
  'nova scotia',
  'nunavut',
  'ontario',
  'prince edward island',
  'quebec',
  'saskatchewan',
  'yukon',
]);

const splitLocationParts = (value) =>
  String(value || '')
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeCountryToken = (token, countryCodes) => {
  const lower = token.toLowerCase();
  const fromCode = countryCodes[token.toUpperCase()];
  if (fromCode) return fromCode;

  if (lower === 'usa' || lower === 'u.s.' || lower === 'u.s.a.') {
    return countryCodes.US || 'united states';
  }

  return lower;
};

const normalizeProvinceToken = (token, regionCodes, allRegionNames) => {
  const upper = token.toUpperCase();
  for (const countryCode of Object.keys(regionCodes)) {
    const regions = regionCodes[countryCode] || {};
    if (Object.prototype.hasOwnProperty.call(regions, upper)) {
      return regions[upper];
    }
  }

  const lower = token.toLowerCase();
  if (allRegionNames.includes(lower)) return lower;
  if (CANADIAN_PROVINCES.has(lower)) return lower;

  return lower.length > 0 ? lower : null;
};

/**
 * Parse country and province/state tokens from company profile fields.
 */
export function parseCompanyLocation(company, referenceData = {}, options = {}) {
  const { inferCountryFromProvince = true } = options;
  const { countryCodes = {}, regionCodes = {} } = referenceData;
  const allRegionNames = [regionCodes.CA, regionCodes.US]
    .filter(Boolean)
    .flatMap((regions) => Object.values(regions));

  const countryOfCompany = new Set();
  const provincesOfCompany = new Set();

  const rawParts = [
    company?.country,
    company?.province,
    company?.locality,
    company?.address,
  ].flatMap(splitLocationParts);

  for (const part of rawParts) {
    const country = normalizeCountryToken(part, countryCodes);
    const knownCountries = new Set([
      ...Object.values(countryCodes),
      'canada',
      'united states',
    ]);
    if (knownCountries.has(country)) {
      countryOfCompany.add(country);
    }

    const province = normalizeProvinceToken(part, regionCodes, allRegionNames);
    if (province && (allRegionNames.includes(province) || CANADIAN_PROVINCES.has(province))) {
      provincesOfCompany.add(province);
    }
  }

  // The app's find_grants derives country from the Country field only. Callers that need
  // 1:1 parity (New Opportunities email) disable this reverse-inference from province.
  if (inferCountryFromProvince) {
    for (const province of provincesOfCompany) {
      if (CANADIAN_PROVINCES.has(province)) {
        countryOfCompany.add(countryCodes.CA || 'canada');
      }
      if (regionCodes.US && Object.values(regionCodes.US).includes(province)) {
        countryOfCompany.add(countryCodes.US || 'united states');
      }
    }
  }

  return {
    countryOfCompany: [...countryOfCompany],
    provincesOfCompany: [...provincesOfCompany],
  };
}

/**
 * Whether a grant's eligible regions match a company's country and/or province.
 */
export function matchesGrantRegions(grantRegions, locationProfile, referenceData = {}) {
  const { countryOfCompany = [], provincesOfCompany = [] } = locationProfile || {};
  const { countryCodes = {} } = referenceData;
  const countryNames = Object.values(countryCodes);

  if ((!countryOfCompany.length && !provincesOfCompany.length) || !grantRegions.length) {
    return true;
  }

  if (grantRegions.some((region) => countryOfCompany.includes(region))) {
    return true;
  }

  if (grantRegions.some((region) => provincesOfCompany.includes(region))) {
    return true;
  }

  if (
    provincesOfCompany.length > 0 &&
    grantRegions.some((region) => countryNames.includes(region))
  ) {
    return grantRegions.some(
      (region) => countryNames.includes(region) && countryOfCompany.includes(region)
    );
  }

  return false;
}

export function parseGrantRegions(grant) {
  const raw = grant?.fields?.Eligible_Region;
  return (
    Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : []
  )
    .map((region) => String(region || '').trim().toLowerCase())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse currency/amount strings to numbers.
 * "$50,000" -> 50000, 1234 -> 1234, null -> 0
 */
export function parseAmount(val) {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  return parseFloat(String(val).replace(/[$,]/g, '')) || 0;
}

/**
 * Years a company has been in operation from its founding year.
 */
export function getYearsInOperation(companyFoundedYear, currentYear = new Date().getFullYear()) {
  const founded = parseFloat(companyFoundedYear);
  if (isNaN(founded)) return NaN;
  return currentYear - founded;
}

/**
 * Whether a company meets a grant's Eligible_Founded requirement.
 * Eligible_Founded stores required minimum years in operation (0 = no requirement).
 */
export const COMPANY_TYPE_OPTIONS = [
  'Sole Proprietorship',
  'Partnership',
  'Corporation',
  'Limited Liability Partnership (LLP)',
  'Cooperative',
  'Non-profit',
  'Trust',
];

export const YES_GATE_OPTIONS = [
  { value: '', label: 'Any company' },
  { value: 'Yes', label: 'Required' },
];

function isRequiredYes(value) {
  return String(value || '').trim().toLowerCase() === 'yes';
}

/** Eligible_Company_Type, Eligible_Women, and Eligible_Indigenous. Empty means no extra gate. */
export function meetsDesignationRequirement(company, grantFields) {
  if (isRequiredYes(grantFields?.Eligible_Women) && company?.womenLed !== true) return false;
  if (isRequiredYes(grantFields?.Eligible_Indigenous) && company?.indigenousTeamMember !== true) return false;
  const requiredTypes = String(grantFields?.Eligible_Company_Type || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter((item) => item && item !== 'all' && item !== 'any');
  if (!requiredTypes.length) return true;
  const companyType = String(company?.companyType || '').trim().toLowerCase();
  return requiredTypes.includes(companyType);
}

export function meetsYearsInOperationRequirement(
  companyFoundedYear,
  requiredYears,
  currentYear = new Date().getFullYear()
) {
  const required = parseFloat(requiredYears);
  if (isNaN(required) || required <= 0) return true;
  const yearsInOperation = getYearsInOperation(companyFoundedYear, currentYear);
  if (isNaN(yearsInOperation)) return true;
  return yearsInOperation >= required;
}

// ---------------------------------------------------------------------------
// Main eligibility function
// ---------------------------------------------------------------------------

/**
 * Filter an array of grants down to those a company is eligible for.
 *
 * @param {Object}  company                  - Company profile
 * @param {string}  company.country          - Country code(s), comma-separated (e.g. "CA" or "CA,US")
 * @param {string}  company.province         - Province/state code(s), comma-separated (e.g. "ON")
 * @param {string}  [company.locality]       - City/locality (currently unused, reserved)
 * @param {string|number} company.founded    - Founding year (e.g. 2018)
 * @param {string}  company.industry         - Industry, comma-separated (e.g. "Technology")
 * @param {string|number} company.employees  - Employee count
 * @param {string}  company.profitable       - "Yes" or "No"
 * @param {Array}   grants                   - Full array of grant records ({ id, fields: { ... } })
 * @param {Object}  referenceData            - Lookup tables
 * @param {Object}  referenceData.countryCodes  - { "CA": "canada", ... }
 * @param {Object}  referenceData.regionCodes   - { CA: { "ON": "ontario", ... }, US: { ... } }
 * @param {Object}  referenceData.industries    - { "Technology": ["saas", ...], ... }
 * @param {Object}  [options]
 * @param {string}  [options.typeOfMoney='both'] - 'both' returns grant array, 'sum' returns total expected value
 * @returns {Array|number} Filtered grants (or sum of expected values if typeOfMoney='sum')
 */
export function findEligibleGrants(company, grants, referenceData, options = {}) {
  const { countryCodes = {}, regionCodes = {}, industries = {} } = referenceData;
  const { typeOfMoney = 'both', inferCountryFromProvince = true } = options;

  grants = filterActiveGrants(grants);

  if (!grants || grants.length === 0) return typeOfMoney === 'sum' ? 0 : [];

  // -- Normalize company inputs -----------------------------------------------

  const locationProfile = parseCompanyLocation(company, referenceData, {
    inferCountryFromProvince,
  });

  // Normalize industry to lowercase array
  const companyIndustry = (typeof company.industry === 'string' ? company.industry : '')
    .trim()
    .toLowerCase()
    .split(',')
    .map((el) => el.trim())
    .filter(Boolean);

  const companyFounded = parseFloat(company.founded);
  const companyEmployees = parseFloat(company.employees);

  // -- Filter grants ----------------------------------------------------------

  const eligible = [];

  for (let i = 0; i < grants.length; i++) {
    const grant = grants[i];
    const f = grant?.fields || {};

    // CHECK 1: Industry match
    // Grant industry "All" matches everything. Otherwise uses the sub-industry
    // taxonomy to check overlap (e.g. company "SaaS" and grant "Technology"
    // both appear under the same parent category).
    const grantIndustry = (typeof f.Eligible_Industry === 'string'
      ? f.Eligible_Industry.toLowerCase().split(',').map((el) => el.trim()).filter(Boolean)
      : []);

    let industryCheck;
    if (!companyIndustry.length || !companyIndustry[0]) {
      industryCheck = true; // no company industry -> pass
    } else {
      if (grantIndustry[0] === companyIndustry[0] || grantIndustry[0] === 'all') {
        industryCheck = true;
      } else {
        const industryKeys = Object.keys(industries);
        industryCheck = industryKeys.some((key) => {
          const subs = Array.isArray(industries[key]) ? industries[key] : [];
          return (
            subs.some((item) => grantIndustry.includes(item)) &&
            subs.some((item) => companyIndustry.includes(item))
          );
        });
      }
    }

    // CHECK 2: Required years in operation (Eligible_Founded; 0 = no requirement)
    const foundedCheck = meetsYearsInOperationRequirement(companyFounded, f.Eligible_Founded);

    // CHECK 3: Location (country + province)
    const grantRegions = parseGrantRegions(grant);
    const locationCheck = matchesGrantRegions(grantRegions, locationProfile, referenceData);

    // CHECK 4: Employee count
    const employeesCheck =
      isNaN(companyEmployees) || !f.Eligible_Employees
        ? true
        : parseFloat(f.Eligible_Employees) <= companyEmployees;

    // CHECK 5: Revenue (estimated as employees × $100,000)
    const revenueCheck =
      isNaN(companyEmployees) || !f.Eligible_Revenue
        ? true
        : parseFloat(f.Eligible_Revenue) <= companyEmployees * 100000;

    // CHECK 6: Favorite (placeholder — always true)
    const favoriteCheck = true;

    // CHECK 7: Grant vs Loan type (placeholder — always true)
    const typeCheck = true;

    // CHECK 8: Profitability
    const profitabilityCheck =
      f.Eligible_Profitable === 'No' ? true : company.profitable === 'Yes';

    // CHECK 9: Not closed
    const openOrClosedCheck = f.Deadline !== 'Closed';

    // CHECK 10: Time estimate ≤ 90 days (or missing)
    const timeEst = f.TimeEstimateToComplete;
    const timeCheck = timeEst == null || timeEst === '' || parseFloat(timeEst) <= 90;

    const designationCheck = meetsDesignationRequirement(company, f);

    // -- All checks must pass -------------------------------------------------
    const allPass =
      industryCheck &&
      foundedCheck &&
      locationCheck &&
      employeesCheck &&
      revenueCheck &&
      favoriteCheck &&
      typeCheck &&
      profitabilityCheck &&
      openOrClosedCheck &&
      timeCheck &&
      designationCheck;

    if (allPass) {
      const pSuccess = parseFloat(f.pSuccess) || 0.65;
      const amount = parseAmount(f.Amount);

      eligible.push({
        ...grant,
        fields: {
          ...f,
          AnnualMargin: amount,
          ExpectedValue: amount * pSuccess,
          id: grant.id || grant.recordId || i,
        },
      });
    }
  }

  // -- Return -----------------------------------------------------------------

  if (typeOfMoney === 'sum') {
    return eligible.reduce((sum, g) => {
      const ev =
        g.fields?.ExpectedValue ||
        parseAmount(g.fields?.Amount) * (parseFloat(g.fields?.pSuccess) || 0.65);
      return sum + ev;
    }, 0);
  }

  return eligible;
}

// ---------------------------------------------------------------------------
// Legacy-compatible wrapper (matches original find_grants signature)
// ---------------------------------------------------------------------------

/**
 * Drop-in replacement for the original find_grants() positional-argument API.
 * Import { countryCodes, regionCodes, industries } from helperData.js and pass them in,
 * or import this from a module that has them in scope.
 *
 * @example
 *   import { createFindGrants } from './grantEligibility';
 *   import { countryCodes, regionCodes, industries } from '../helperData';
 *   const find_grants = createFindGrants({ countryCodes, regionCodes, industries });
 *   const results = find_grants(country, province, locality, founded, industry, employees, profitable, grants);
 */
export function createFindGrants(referenceData) {
  return function find_grants(
    country,
    province,
    locality,
    founded,
    industry,
    employees,
    profitable,
    grants,
    typeOfMoney = 'both',
    companyType = '',
    indigenousTeamMember = false,
    womenLed = false
  ) {
    return findEligibleGrants(
      { country, province, locality, founded, industry, employees, profitable, companyType, indigenousTeamMember, womenLed },
      grants,
      referenceData,
      { typeOfMoney }
    );
  };
}
